import {minuteInMS, RunInAction_Set} from "web-vcore";
import {Assert, Clone} from "js-vextensions";
import {EngineConfig_DreamTranscribe, TranscribeTarget} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_DreamTranscribe.js";
import {JournalSegment} from "../../../Server/DB/journalEntries/@JournalEntry.js";
import {store} from "../../../Store/index.js";
import {nativeBridge} from "../../../Utils/Bridge/Bridge_Native.js";
import {BaseSpanEffects, ExtractBracketedTextFromSpanText, ProcessTranscribedSpans, SpanEffect_NearTTS, SpansToText, StripBracketedTextFromSpanText, TranscribedSpan, TranscriptionChunkInfo} from "../../../Utils/Bridge/Whisper.js";
import {UpdateJournalEntry} from "../../../Server/Commands/JournalEntryCommands.js";
import {GetKeySegmentsInCurrentDream, TextToWordsOrTargets} from "./DreamTranscribeComp.js";
import {FormatFullTimestamp, FormatLiteTimestamp} from "../../../Utils/TimestampHandler.js";

export type AdvancedTranscriptionHandler_Config = Pick<EngineConfig_DreamTranscribe,
	| "markTimeEveryXMinutes"
	| "timestamps_liteMarkersInBetween"
	| "bracketWordsNearTTS"
	| "bracketWordsNearTTS_noSpeakCountOrVibrate"
	| "bracketWordsNearTTS_noDJ"
	| "onTranscribe_speakWordCount"
	| "onTranscribe_vibrateDuration"
	| "onTranscribe_vibrateStrength"
>;

export class AdvancedTranscriptionHandler {
	constructor(data: Pick<AdvancedTranscriptionHandler, "getConfig" | "getTranscribeTarget" | "narrateText">) {
		Object.assign(this, data);
	}

	getTranscribeTarget!: ()=>TranscribeTarget;
	getConfig!: ()=>AdvancedTranscriptionHandler_Config;
	narrateText?: (text: string)=>void;

	/** Only set to latest chunk if it was valid (ie. non-zero length after SpansToText processing below), and saving to the target succeeded. */
	lastChunkAddedToTranscribeTarget: TranscriptionChunkInfo|n;
	/** Set to chunkStartTime whenever a full [HH:MM:SS] timestamp is successfully added. Used to compute lite @X markers. */
	lastFullTimestampChunkStartTime: number|n;

	HandleChunk(info: TranscriptionChunkInfo) {
		const c = this.getConfig();
		const {spans, chunkStartTime, chunkEndTime} = info;
		const text_withBracketing = SpansToText(ProcessTranscribedSpans(spans, [
			...BaseSpanEffects(),
			new SpanEffect_NearTTS({maxDist: c.bracketWordsNearTTS * 1000, bracket_wholeSpan: true}),
		]));
		const text_withBracketing_strippedText = StripBracketedTextFromSpanText(text_withBracketing);
		const text_withBracketing_bracketedText = ExtractBracketedTextFromSpanText(text_withBracketing);

		if (text_withBracketing.length > 0) {
			const {journalEntry, transcribeTargetSegment} = GetKeySegmentsInCurrentDream();

			const transcribeTarget = this.getTranscribeTarget();
			let transcribeTarget_addFullTimestamp: boolean;
			// -2 is the only setting value that can disable timestamp-insertion into the transcribe-panel
			if (c.markTimeEveryXMinutes == -2) transcribeTarget_addFullTimestamp = false;
			else if (c.markTimeEveryXMinutes == -1) transcribeTarget_addFullTimestamp = false;
			else if (c.markTimeEveryXMinutes == 0) transcribeTarget_addFullTimestamp = true;
			else if (this.lastChunkAddedToTranscribeTarget == null) transcribeTarget_addFullTimestamp = true;
			else {
				const msPerBin = minuteInMS * c.markTimeEveryXMinutes;
				const lastChunkMinuteBin = Math.floor(this.lastChunkAddedToTranscribeTarget.chunkStartTime / msPerBin);
				const currentChunkMinuteBin = Math.floor(chunkStartTime / msPerBin);
				transcribeTarget_addFullTimestamp = lastChunkMinuteBin != currentChunkMinuteBin;
			}
			const fullTimestampStr = FormatFullTimestamp(chunkStartTime);
			const liteTimestampStr_ifValid = (!transcribeTarget_addFullTimestamp && c.timestamps_liteMarkersInBetween && this.lastFullTimestampChunkStartTime != null)
				? FormatLiteTimestamp(chunkStartTime, this.lastFullTimestampChunkStartTime) : "";
			const timestampStr_forTarget = transcribeTarget_addFullTimestamp ? fullTimestampStr : liteTimestampStr_ifValid;

			// for panel-as-secondary-target
			const uiState = store.main.engine.journey.transcribe;
			const panelText_prefixSpacer = uiState.transcribeText.match(/\s$/) ? "" : "\n";
			const timestampStr_forSecondary = c.markTimeEveryXMinutes != -2 ? fullTimestampStr : "";

			if (transcribeTarget == TranscribeTarget.panel) {
				RunInAction_Set(()=>uiState.transcribeText += `${timestampStr_forTarget}${text_withBracketing}\n`);
				if (transcribeTarget_addFullTimestamp) this.lastFullTimestampChunkStartTime = chunkStartTime;
				this.lastChunkAddedToTranscribeTarget = info;
			} else {
				// for any bracketed-text (which can't go to DJ), strip it out, and add it to transcribe-panel's text instead (with timestamp, unless blocked with -2 setting)
				let text_withBracketing_forDJ = text_withBracketing;
				if (c.bracketWordsNearTTS_noDJ) {
					text_withBracketing_forDJ = text_withBracketing_strippedText;
					if (text_withBracketing_bracketedText.length > 0) {
						RunInAction_Set(()=>uiState.transcribeText += `${panelText_prefixSpacer}${timestampStr_forSecondary}{${text_withBracketing_bracketedText}}\n`);
					}
				}

				// ensure that after bracketed-text stripping, there's still text to send to DJ
				if (text_withBracketing_forDJ.length > 0) {
					Assert(journalEntry && transcribeTargetSegment, "These shouldn't be null. (if null, we should have redirected to branch above)");
					const segmentIndex = journalEntry.segments.indexOf(transcribeTargetSegment);
					const segments_new = Clone(journalEntry.segments) as JournalSegment[];
					const segment_newData = segments_new[segmentIndex];
					segment_newData.longText = (segment_newData.longText ?? "") + (segment_newData.longText?.length ? "\n" : "") + timestampStr_forTarget + text_withBracketing_forDJ;

					let updateCompleted = false;
					try {
						new UpdateJournalEntry({id: journalEntry.id, updates: {segments: segments_new}}).RunOnServer();
						updateCompleted = true;
						if (transcribeTarget_addFullTimestamp) this.lastFullTimestampChunkStartTime = chunkStartTime;
						this.lastChunkAddedToTranscribeTarget = info;
					} finally {
						// if saving the transcription chunk to the DJ failed (eg. network down), save it to the panel instead
						if (!updateCompleted) {
							RunInAction_Set(()=>uiState.transcribeText += `${panelText_prefixSpacer}${timestampStr_forSecondary}{${text_withBracketing_forDJ}}\n`);
						}
					}
				}
			}
		}

		const textForSpeakCountAndVibrate = c.bracketWordsNearTTS_noSpeakCountOrVibrate ? text_withBracketing_strippedText : text_withBracketing;
		const wordCount = TextToWordsOrTargets(textForSpeakCountAndVibrate, false).length;
		if (wordCount > 0) {
			if (c.onTranscribe_speakWordCount) {
				this.narrateText?.(wordCount + "");
			}
			if (c.onTranscribe_vibrateDuration > 0) {
				nativeBridge.Call("VibratePhone", c.onTranscribe_vibrateDuration * 1000, c.onTranscribe_vibrateStrength);
			}
		}
	}
}